import type { TickerConfig } from "@/types/ticker";

/**
 * Compile-time registry of supported underlyings. Mirrors the seed data in
 * `supabase/migrations/20260501000_tickers_registry.sql`. Engine paths
 * (`defaultParams`, `roundToStrike`, `effectiveSpot`) need synchronous
 * `strike_step` lookup on every tick — async-fetching this from the DB on
 * every render would race the live-quote effect. The DB table is canonical;
 * this constant is the synchronous mirror.
 *
 * When adding a new ticker:
 *   1. Insert a row in a new migration.
 *   2. Add the matching object to `BUILT_IN_TICKERS` below.
 *   3. Both must agree, or the engine and the user-facing picker will
 *      disagree about what ticker the user is on.
 */
const BUILT_IN_TICKERS: ReadonlyArray<TickerConfig> = [
  {
    symbol: "SPX",
    display_name: "S&P 500 Index",
    kind: "index",
    strike_step: 5,
    vol_index_symbol: "VIX",
    expiry_kind: "european",
    // ^GSPC is the cash index — Yahoo exposes ZERO options groups for it.
    // ^SPX is the SPX-options ticker (48 expiries); see the 2026-05-08
    // postmortem in dossier/INFRA.md.
    yahoo_symbol: "^SPX",
    active: true,
  },
  {
    symbol: "XSP",
    display_name: "Mini-SPX Index",
    kind: "index",
    strike_step: 1,
    vol_index_symbol: "VIX",
    expiry_kind: "european",
    yahoo_symbol: "^XSP",
    active: true,
  },
  {
    symbol: "SPY",
    display_name: "SPDR S&P 500 ETF",
    kind: "etf",
    strike_step: 1,
    vol_index_symbol: "VIX",
    expiry_kind: "american",
    yahoo_symbol: "SPY",
    active: true,
  },
  {
    symbol: "QQQ",
    display_name: "Invesco QQQ Trust",
    kind: "etf",
    strike_step: 1,
    vol_index_symbol: "VXN",
    expiry_kind: "american",
    yahoo_symbol: "QQQ",
    active: true,
  },
  {
    symbol: "IWM",
    display_name: "iShares Russell 2000 ETF",
    kind: "etf",
    strike_step: 1,
    vol_index_symbol: "RVX",
    expiry_kind: "american",
    yahoo_symbol: "IWM",
    active: true,
  },
  {
    symbol: "NDX",
    display_name: "Nasdaq-100 Index",
    kind: "index",
    strike_step: 25,
    vol_index_symbol: "VXN",
    expiry_kind: "european",
    yahoo_symbol: "^NDX",
    active: true,
  },
  {
    symbol: "RUT",
    display_name: "Russell 2000 Index",
    kind: "index",
    strike_step: 5,
    vol_index_symbol: "RVX",
    expiry_kind: "european",
    yahoo_symbol: "^RUT",
    active: true,
  },
  {
    symbol: "AAPL",
    display_name: "Apple Inc.",
    kind: "equity",
    strike_step: 1,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "AAPL",
    active: true,
  },
  {
    symbol: "NVDA",
    display_name: "NVIDIA Corporation",
    kind: "equity",
    strike_step: 2.5,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "NVDA",
    active: true,
  },
  {
    symbol: "TSLA",
    display_name: "Tesla, Inc.",
    kind: "equity",
    strike_step: 5,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "TSLA",
    active: true,
  },
  {
    symbol: "MSFT",
    display_name: "Microsoft Corporation",
    kind: "equity",
    strike_step: 5,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "MSFT",
    active: true,
  },
  {
    symbol: "GOOGL",
    display_name: "Alphabet Inc. (Class A)",
    kind: "equity",
    strike_step: 2.5,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "GOOGL",
    active: true,
  },
  {
    symbol: "AMZN",
    display_name: "Amazon.com, Inc.",
    kind: "equity",
    strike_step: 2.5,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "AMZN",
    active: true,
  },
  {
    symbol: "META",
    display_name: "Meta Platforms, Inc.",
    kind: "equity",
    strike_step: 5,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "META",
    active: true,
  },
  {
    symbol: "NFLX",
    display_name: "Netflix, Inc.",
    kind: "equity",
    strike_step: 10,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "NFLX",
    active: true,
  },
  {
    symbol: "MU",
    display_name: "Micron Technology, Inc.",
    kind: "equity",
    strike_step: 2.5,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "MU",
    active: true,
  },
  {
    symbol: "AMD",
    display_name: "Advanced Micro Devices, Inc.",
    kind: "equity",
    strike_step: 2.5,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "AMD",
    active: true,
  },
  {
    symbol: "INTC",
    display_name: "Intel Corporation",
    kind: "equity",
    strike_step: 1,
    vol_index_symbol: null,
    expiry_kind: "american",
    yahoo_symbol: "INTC",
    active: true,
  },
  {
    symbol: "VIX",
    display_name: "CBOE Volatility Index",
    kind: "index",
    strike_step: 1,
    vol_index_symbol: null,
    expiry_kind: "european",
    yahoo_symbol: "^VIX",
    active: false,
  },
  {
    symbol: "VXN",
    display_name: "CBOE Nasdaq-100 Volatility Index",
    kind: "index",
    strike_step: 1,
    vol_index_symbol: null,
    expiry_kind: "european",
    yahoo_symbol: "^VXN",
    active: false,
  },
  {
    symbol: "RVX",
    display_name: "CBOE Russell 2000 Volatility Index",
    kind: "index",
    strike_step: 1,
    vol_index_symbol: null,
    expiry_kind: "european",
    yahoo_symbol: "^RVX",
    active: false,
  },
];

// Runtime-mutable map. Compile-time `BUILT_IN_TICKERS` seeds it on
// module load so the strategy engine's synchronous lookups
// (`getStrikeStep`, `getTickerConfig`) work from first paint without
// waiting on the network. `TickerRegistryProvider` augments the map at
// runtime from /api/v1/tickers + UserChannel `ticker.discovered`
// broadcasts so auto-discovered tickers become visible to the engine
// and picker.
const REGISTRY: Map<string, TickerConfig> = new Map(
  BUILT_IN_TICKERS.map((t) => [t.symbol, t]),
);

const registryListeners = new Set<() => void>();

// Snapshot caches for useSyncExternalStore — must return the same array
// reference between calls or React will infinite-loop. Invalidated by
// notifyRegistry() whenever a ticker is added or replaced.
let activeSnapshot: ReadonlyArray<TickerConfig> | null = null;
let allSnapshot: ReadonlyArray<TickerConfig> | null = null;

function notifyRegistry(): void {
  activeSnapshot = null;
  allSnapshot = null;
  registryListeners.forEach((cb) => cb());
}

/** Insert or replace a ticker in the runtime registry and notify listeners. */
export function registerTicker(cfg: TickerConfig): void {
  const existing = REGISTRY.get(cfg.symbol);
  if (existing && shallowEqualConfig(existing, cfg)) return;
  REGISTRY.set(cfg.symbol, cfg);
  notifyRegistry();
}

/** Bulk-replace from /api/v1/tickers. Compile-time entries are kept intact
 *  for the curated tickers; auto-added rows are appended. */
export function hydrateRegistry(rows: ReadonlyArray<TickerConfig>): void {
  let changed = false;
  for (const row of rows) {
    const existing = REGISTRY.get(row.symbol);
    if (existing && shallowEqualConfig(existing, row)) continue;
    REGISTRY.set(row.symbol, row);
    changed = true;
  }
  if (changed) notifyRegistry();
}

/** Subscribe to registry mutations. Returns an unsubscribe function. Used
 *  by the picker so the typeahead re-renders when new tickers land. */
export function subscribeRegistry(cb: () => void): () => void {
  registryListeners.add(cb);
  return () => {
    registryListeners.delete(cb);
  };
}

function shallowEqualConfig(a: TickerConfig, b: TickerConfig): boolean {
  return (
    a.symbol === b.symbol &&
    a.display_name === b.display_name &&
    a.kind === b.kind &&
    a.strike_step === b.strike_step &&
    a.vol_index_symbol === b.vol_index_symbol &&
    a.expiry_kind === b.expiry_kind &&
    a.yahoo_symbol === b.yahoo_symbol &&
    a.active === b.active
  );
}

export const DEFAULT_TICKER = "SPX" as const;

export function getTickerConfig(symbol: string): TickerConfig {
  const cfg = REGISTRY.get(symbol);
  if (!cfg) {
    throw new Error(
      `Unknown ticker: ${symbol}. Seed it in db/seeds/tickers.rb (and mirror in lib/tickers/registry.ts), or add it via the picker's discover flow.`,
    );
  }
  return cfg;
}

/** Like getTickerConfig but returns null instead of throwing — for UI paths
 *  that may render before the runtime registry has hydrated. */
export function tryGetTickerConfig(symbol: string): TickerConfig | null {
  return REGISTRY.get(symbol) ?? null;
}

// Vol indices are kept active in the DB so the cron jobs scrape their
// quotes for IV resolution, but they are not tradable underlyings and
// must not appear in the picker. Excluded by symbol so the picker stays
// decoupled from the `active` flag the scheduler reads.
const PICKER_HIDDEN_SYMBOLS: ReadonlySet<string> = new Set([
  "VIX",
  "VXN",
  "RVX",
]);

/** Tickers shown in the user-facing picker. Snapshot is cached so that
 *  useSyncExternalStore readers see a stable reference between renders. */
export function getActiveTickers(): ReadonlyArray<TickerConfig> {
  if (activeSnapshot) return activeSnapshot;
  activeSnapshot = Object.freeze(
    Array.from(REGISTRY.values()).filter(
      (t) => t.active && !PICKER_HIDDEN_SYMBOLS.has(t.symbol),
    ),
  );
  return activeSnapshot;
}

/** All registered tickers, including inactive ones (e.g. vol indices). */
export function getAllTickers(): ReadonlyArray<TickerConfig> {
  if (allSnapshot) return allSnapshot;
  allSnapshot = Object.freeze(Array.from(REGISTRY.values()));
  return allSnapshot;
}

// Strike step for a ticker, with a graceful fallback for symbols that
// haven't reached the runtime registry yet (e.g. simulator tabs / saved
// strategies referencing an auto-discovered ticker that the FE picker
// will register on first /api/v1/tickers hydrate). Default of 1 mirrors
// what Tickers::Discoverer seeds provisionally before the chain refresh
// auto-detects the actual step.
export function getStrikeStep(symbol: string): number {
  return tryGetTickerConfig(symbol)?.strike_step ?? 1;
}

/**
 * Validate a ticker for use in the backtest pipeline. Returns either the
 * resolved config or a `{ error, status }` shape ready to be wrapped in a
 * `NextResponse.json`. Two failure modes:
 *   - unknown ticker (not in the registry) → 400
 *   - equity ticker with no paired vol index → 400 with the supported set
 *
 * The `feature` argument is folded into the equity-rejection message so
 * each caller can say "Backtests for…" / "Strategy generation for…" /
 * "Trade plans for…" without re-inventing the prose.
 */
export function assertBacktestTicker(
  symbol: string,
  feature: string,
): { config: TickerConfig; volIndex: string } | { error: string; status: 400 } {
  let config: TickerConfig;
  try {
    config = getTickerConfig(symbol);
  } catch {
    return { error: `Unknown ticker: ${symbol}`, status: 400 };
  }
  if (config.vol_index_symbol === null) {
    return {
      error: `${feature} for equity tickers (no paired vol index) is not yet supported. Choose SPX, SPY, QQQ, IWM, NDX, or RUT.`,
      status: 400,
    };
  }
  return { config, volIndex: config.vol_index_symbol };
}
